import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, User, Building, Camera, Check, MapPin } from "lucide-react";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS",
  "KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY",
  "NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"
];

// Simple checkbox visual
function CheckBox({ checked }) {
  if (checked) {
    return (
      <div className="w-4 h-4 rounded-sm bg-white flex items-center justify-center">
        <Check className="w-3 h-3 text-black" />
      </div>
    );
  }
  return <div className="w-4 h-4 rounded-sm border-2 border-white/40" />;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [isAssistant, setIsAssistant] = useState(true);
  const [isStudio, setIsStudio] = useState(false);
  const [loading, setLoading] = useState(false);

  // Set initial roles from URL params
  useEffect(() => {
    const urlRole = searchParams.get("role");
    if (urlRole === "studio") {
      setIsStudio(true);
    } else if (urlRole === "assistant") {
      setIsAssistant(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Build roles array
    const roles = [];
    if (isAssistant) roles.push("assistant");
    if (isStudio) roles.push("studio");
    
    if (roles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error("Password must contain at least one uppercase letter");
      return;
    }
    if (!/[0-9]/.test(password)) {
      toast.error("Password must contain at least one number");
      return;
    }

    if (!city.trim()) {
      toast.error("City is required — studios need your location to find you");
      return;
    }
    if (!state) {
      toast.error("State is required — studios need your location to find you");
      return;
    }

    setLoading(true);

    try {
      await register(name, email, password, roles, city.trim(), state);
      if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: roles.join(',') });
      toast.success("Account created successfully!");
      navigate("/dashboard");
    } catch (error) {
      const errorMsg = error.message || "Registration failed";
      if (errorMsg.toLowerCase().includes("already registered")) {
        toast.error(
          <div>
            {errorMsg}
            <button 
              onClick={() => navigate('/login')} 
              className="ml-2 underline font-medium"
            >
              Go to Login
            </button>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <SEO
        title="Sign Up"
        description="Create your free GottaSecond account. Studios: post jobs and hire second shooters. Photographers: get discovered by studios in your area."
        path="/register"
      />
      {/* Left Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1615456825800-b6d3447be302?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxjaW5lbWElMjBjYW1lcmElMjBsZW5zJTIwZ2VhciUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcwMTk1NDM5fDA&ixlib=rb-4.1.0&q=85"
          alt="Camera lens"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#0a0a0a] to-transparent" />
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
        <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-12" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider">Back to Home</span>
        </Link>

        <div className="max-w-md">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
            Join GottaSecond
          </h1>
          <p className="text-white/50 text-lg mb-10">
            Create your account to get started
          </p>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection - Multiple Choice */}
            <div className="space-y-3">
              <Label className="text-sm text-white/60 uppercase tracking-wider">
                I want to (select all that apply)
              </Label>
              <div className="space-y-3">
                <div 
                  className={`flex items-center gap-4 p-4 bg-white/[0.05] border rounded-sm cursor-pointer transition-all ${isAssistant ? 'border-white/40 bg-white/5' : 'border-white/10'}`}
                  onClick={() => setIsAssistant(!isAssistant)}
                  data-testid="role-assistant"
                >
                  <CheckBox checked={isAssistant} />
                  <Camera className="w-5 h-5 text-white/60" />
                  <div className="flex-1">
                    <span className="font-medium">Find Work as Second Shooter</span>
                    <p className="text-white/50 text-sm">Browse jobs and get hired by studios</p>
                  </div>
                </div>
                
                <div 
                  className={`flex items-center gap-4 p-4 bg-white/[0.05] border rounded-sm cursor-pointer transition-all ${isStudio ? 'border-white/40 bg-white/5' : 'border-white/10'}`}
                  onClick={() => setIsStudio(!isStudio)}
                  data-testid="role-studio"
                >
                  <CheckBox checked={isStudio} />
                  <Building className="w-5 h-5 text-white/60" />
                  <div className="flex-1">
                    <span className="font-medium">Hire Second Shooters</span>
                    <p className="text-white/50 text-sm">Post jobs and find talent for your projects</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm text-white/60 uppercase tracking-wider">
                {isStudio && !isAssistant ? "Studio / Business Name" : "Full Name"}
              </Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={isStudio && !isAssistant ? "Your Studio Name" : "John Doe"}
                  className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-white/60 uppercase tracking-wider">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm text-white/60 uppercase tracking-wider">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                  required
                  minLength={8}
                  data-testid="password-input"
                />
              </div>
              <p className="text-xs text-white/30 mt-1">Min 8 chars, 1 uppercase, 1 number</p>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label className="text-sm text-white/60 uppercase tracking-wider">
                Location *
              </Label>
              <p className="text-xs text-white/30 -mt-1">Studios search by location — this is how they find you</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                    required
                    data-testid="city-input"
                  />
                </div>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="bg-white/[0.05] border border-white/10 focus:border-white/40 text-white h-12 rounded-sm px-4 appearance-none"
                  required
                  data-testid="state-input"
                >
                  <option value="">State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <p className="text-xs text-white/40 leading-relaxed">
              By creating an account, you agree to our{" "}
              <Link to="/terms" className="text-white/60 hover:text-white underline">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" className="text-white/60 hover:text-white underline">Privacy Policy</Link>.
            </p>

            <Button
              type="submit"
              disabled={loading || (!isAssistant && !isStudio)}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold py-3 uppercase tracking-wide disabled:opacity-50"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </form>

          <p className="text-center text-white/50 mt-8">
            Already have an account?{" "}
            <Link to="/login" className="text-white hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
